import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, ShieldCheck, Sparkles, Eye, EyeOff, Home, MessageCircle, MapPin } from "lucide-react";
import LocationSearch from "@/components/locanest/LocationSearch";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import GoogleSignInButton from "@/components/locanest/GoogleSignInButton";

// Password field with show/hide toggle (Section 1, Feb 2026)
export function PasswordInput({ testId, ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={show ? "text" : "password"} data-testid={testId} />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        aria-label={show ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[#F0EBE1] text-[#5C6B7A]"
        data-testid={`${testId}-toggle`}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

// Referral landing banner shown ABOVE the signup form when ?ref= is present.
function ReferralBanner({ code }) {
  return (
    <div className="rounded-lg bg-gradient-to-br from-[#F0EBE1] to-[#D4DEC6]/50 border border-[#2B3A67]/20 p-5 mb-6" data-testid="referral-banner">
      <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#C85A32] font-semibold">
        <Gift size={13} /> You&rsquo;ve been invited to LocaNest
      </div>
      <div className="font-display text-2xl mt-1.5">Your friend&rsquo;s code is <span className="text-[#C85A32]">{code}</span></div>
      <p className="text-sm text-[#1A1F2C] mt-2 leading-relaxed">
        Sign up and complete your first paid booking — both of you get <strong>30 days of LocaNest Access</strong> free (unlocks owner contact on every listing).
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1.5"><Home size={12} className="text-[#2B3A67]" /> Verified rentals across India</div>
        <div className="flex items-center gap-1.5"><ShieldCheck size={12} className="text-[#2B3A67]" /> Owner details protected</div>
        <div className="flex items-center gap-1.5"><MessageCircle size={12} className="text-[#2B3A67]" /> Chat directly with owners</div>
        <div className="flex items-center gap-1.5"><Sparkles size={12} className="text-[#C85A32]" /> No brokerage drama</div>
      </div>
    </div>
  );
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "tenant", referral_code: "", preferred_city: "", preferred_locality: "" });
  const [locLabel, setLocLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const ref = (params.get("ref") || "").trim().toUpperCase();
    if (ref) setForm((f) => ({ ...f, referral_code: ref }));
    // eslint-disable-next-line
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form, referral_code: form.referral_code?.trim().toUpperCase() || null };
      if (form.role !== "tenant") { payload.preferred_city = null; payload.preferred_locality = null; }
      const user = await register(payload);
      toast.success(`Welcome to LocaNest, ${user.name.split(" ")[0]}!`);
      if (user.role === "owner") return navigate("/owner");
      if (user.preferred_city) {
        const sp = new URLSearchParams({ city: user.preferred_city });
        if (user.preferred_locality) sp.set("locality", user.preferred_locality);
        return navigate(`/listings?${sp.toString()}`);
      }
      navigate("/listings");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16">
      {form.referral_code && <ReferralBanner code={form.referral_code} />}
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Join LocaNest</div>
      <h1 className="font-display text-4xl mt-2">Create your account</h1>

      <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-[#F0EBE1] rounded-md" data-testid="role-toggle">
        <button
          type="button"
          onClick={() => set("role", "tenant")}
          className={`py-2.5 text-sm rounded ${form.role === "tenant" ? "bg-[#FDFBF7] shadow-sm font-semibold" : "text-[#5C6B7A]"}`}
          data-testid="role-tenant"
        >I'm renting (Tenant)</button>
        <button
          type="button"
          onClick={() => set("role", "owner")}
          className={`py-2.5 text-sm rounded ${form.role === "owner" ? "bg-[#FDFBF7] shadow-sm font-semibold" : "text-[#5C6B7A]"}`}
          data-testid="role-owner"
        >I'm listing (Owner)</button>
      </div>

      <div className="mt-5"><GoogleSignInButton role={form.role} referralCode={form.referral_code?.trim().toUpperCase() || null} label={`Sign up with Google as ${form.role === "owner" ? "Owner" : "Tenant"}`} testId="google-signup-btn" /></div>
      <div className="mt-5 flex items-center gap-3 text-xs text-[#5C6B7A]"><span className="flex-1 h-px bg-[#1A1F2C]/10" /> or use email <span className="flex-1 h-px bg-[#1A1F2C]/10" /></div>

      <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="register-form">
        <div><Label>Full name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="reg-name" /></div>
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" /></div>
        <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91-9876543210" data-testid="reg-phone" /></div>
        <div>
          <Label>Password (min 6 chars)</Label>
          <PasswordInput required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} testId="reg-password" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><Gift size={13} className="text-[#C85A32]" /> Referral code (optional)</Label>
          <Input value={form.referral_code} onChange={(e) => set("referral_code", e.target.value.toUpperCase())} placeholder="ABCD1234" data-testid="reg-referral" />
          {form.referral_code && <div className="text-xs text-[#2B3A67] mt-1">You&apos;ll both get 30 days of Access when your first booking is paid.</div>}
        </div>
        {form.role === "tenant" && (
          <div data-testid="reg-preferred-location">
            <Label className="flex items-center gap-1.5"><MapPin size={13} className="text-[#C85A32]" /> Where are you looking for a rental? (optional)</Label>
            <LocationSearch
              compact
              showGps={false}
              value={locLabel}
              onChange={(t) => { setLocLabel(t); if (!t) { set("preferred_city", ""); set("preferred_locality", ""); } }}
              onPick={(it) => { setLocLabel(it.label); set("preferred_city", it.city || ""); set("preferred_locality", it.locality || ""); }}
              placeholder="City or area — e.g. Vaishali Nagar, Jaipur"
              testId="reg-location"
            />
            {form.preferred_city && <div className="text-xs text-[#2B3A67] mt-1" data-testid="reg-location-confirm">We&rsquo;ll show you rentals in {form.preferred_locality ? `${form.preferred_locality}, ` : ""}{form.preferred_city} right after signup.</div>}
          </div>
        )}
        {error && <div className="text-sm text-red-600" data-testid="reg-error">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md h-11" data-testid="reg-submit">
          {loading ? "Creating…" : "Create account"}
        </Button>
      </form>

      <div className="mt-6 text-sm text-[#5C6B7A]">
        Already have an account? <Link to="/login" className="text-[#C85A32] font-medium">Login</Link>
      </div>
      <div className="mt-6 text-xs text-[#5C6B7A]">
        By creating an account you agree to our <Link to="/terms" className="underline">Terms</Link>, <Link to="/privacy" className="underline">Privacy Policy</Link> &amp; <Link to="/refund-policy" className="underline">Refund Policy</Link>.
      </div>
    </div>
  );
}
